import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentCheckIcon,
  CubeIcon,
  VariableIcon,
  MapPinIcon,
  UserIcon,
  CalendarDaysIcon,
  BuildingStorefrontIcon,
  DocumentCheckIcon,
  ChartBarIcon,
  ArrowTopRightOnSquareIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import {
  ReportSummarySection,
  useServiceReportFinancialSummary,
} from "./report-summary-section";
import RoleOnly from "../../../../routes/roleOnly";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { PMDialog } from "../../../utils/PMDialog";
import {
  fetchReport,
  fetchReportChecklist,
  fetchReportItems,
} from "../../../../api/service/report";

const REPORT_OVERVIEW_CHECKLIST_FIELDS =
  "id,title,description,status,is_items_editable,is_checked,finished_at,items.*";
import { toast } from "react-hot-toast";
import { TinyDocumentSection } from "../../documents/tinyDocumentSection";
import { ApproveReportDialog } from "./approveReportDialog";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { formatUnitDisplay } from "../../../../utils/reportVariableUnits";

const AccordionSection = ({ title, icon: Icon, children, defaultOpen = false, rightContent }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 md:p-5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {Icon && <Icon className="h-5 w-5 text-gray-600 flex-shrink-0" />}
          <span className="font-medium text-gray-900 truncate">{title}</span>
        </div>
        {rightContent && (
          <div className="flex items-center gap-3 ml-4 flex-shrink-0">
            {rightContent}
          </div>
        )}
        {isOpen ? (
          <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0 ml-2" />
        ) : (
          <ChevronRightIcon className="h-5 w-5 text-gray-500 flex-shrink-0 ml-2" />
        )}
      </button>
      {isOpen && <div className="p-4 md:p-5 bg-white border-t border-gray-200">{children}</div>}
    </div>
  );
};

const StatusBadge = ({ status, type = "default" }) => {
  const getStatusConfig = (status, type) => {
    switch (type) {
      case "checklist":
        switch (status) {
          case true:
            return { color: "green", text: "Godkjent", icon: CheckCircleIcon };
          case false:
            return { color: "red", text: "Avvik", icon: XCircleIcon };
          default:
            return { color: "gray", text: "Ikke sjekket", icon: ClockIcon };
        }
      case "approval":
        switch (status) {
          case "APPROVED":
            return { color: "green", text: "Godkjent", icon: CheckCircleIcon };
          case "NOT_APPROVED":
            return { color: "red", text: "Ikke godkjent", icon: XCircleIcon };
          case "PENDING_APPROVAL":
            return { color: "yellow", text: "Venter godkjenning", icon: ClockIcon };
          case "UPDATED":
            return { color: "blue", text: "Oppdatert", icon: ExclamationTriangleIcon };
          default:
            return { color: "gray", text: "Ukjent", icon: ClockIcon };
        }
      default:
        return { color: "blue", text: status || "Ukjent", icon: ClockIcon };
    }
  };

  const config = getStatusConfig(status, type);
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      config.color === "green" ? "bg-green-100 text-green-800" :
      config.color === "red" ? "bg-red-100 text-red-800" :
      config.color === "yellow" ? "bg-yellow-100 text-yellow-800" :
      config.color === "blue" ? "bg-blue-100 text-blue-800" :
      "bg-gray-100 text-gray-800"
    }`}>
      <Icon className="h-3 w-3" />
      {config.text}
    </div>
  );
};

const ReportOverviewSummary = ({ open, onClose, reportId, currentUser, onRefresh }) => {
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [overviewChecklist, setOverviewChecklist] = useState(null);
  const [overviewChecklistLoading, setOverviewChecklistLoading] = useState(false);
  const [overviewItems, setOverviewItems] = useState([]);
  const [overviewItemsLoading, setOverviewItemsLoading] = useState(false);

  const canManageReports = useUserHasAccess({ role: "service", action: "manage" });
  const canApproveReports = useUserHasAccess({ role: "service", action: "approve" }) || canManageReports;

  const {
    summary: reportFinancialSummary,
    loading: reportFinancialSummaryLoading,
  } = useServiceReportFinancialSummary(
    open && reportId ? reportId : null,
    report?.updated_at
  );

  // Load report data when dialog opens or reportId changes
  useEffect(() => {
    if (open && reportId) {
      loadReport();
    } else if (!open) {
      // Clear data when dialog closes
      setReport(null);
      setError(null);
      setApproveDialogOpen(false);
      setOverviewChecklist(null);
      setOverviewChecklistLoading(false);
      setOverviewItems([]);
      setOverviewItemsLoading(false);
    }
  }, [open, reportId]);

  const reportChecklistId = report?.checklist_id ?? report?.checklistId;

  useEffect(() => {
    if (!open || !reportId) return;

    let cancelled = false;
    setOverviewItemsLoading(true);
    fetchReportItems(reportId)
      .then((res) => {
        if (!cancelled) setOverviewItems(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setOverviewItems([]);
      })
      .finally(() => {
        if (!cancelled) setOverviewItemsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, reportId, report?.updated_at]);

  useEffect(() => {
    if (!open || !reportId || !reportChecklistId) {
      setOverviewChecklist(null);
      setOverviewChecklistLoading(false);
      return;
    }

    let cancelled = false;
    setOverviewChecklistLoading(true);
    setOverviewChecklist(null);

    fetchReportChecklist(reportId, { fields: REPORT_OVERVIEW_CHECKLIST_FIELDS })
      .then((response) => {
        if (cancelled) return;
        if (response?.errors) {
          const benign = response.errors.some(
            (err) =>
              typeof err === "string" &&
              (err.includes("does not have a checklist") || err.includes("404"))
          );
          setOverviewChecklist(null);
          if (!benign) console.error("ReportOverviewSummary checklist:", response.errors);
          return;
        }
        const checklistData = response?.data || response;
        if (checklistData?.id) {
          if (!checklistData.items || !Array.isArray(checklistData.items)) {
            checklistData.items = [];
          }
          setOverviewChecklist(checklistData);
        } else {
          setOverviewChecklist(null);
        }
      })
      .finally(() => {
        if (!cancelled) setOverviewChecklistLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, reportId, reportChecklistId, report?.updated_at]);

  const loadReport = async () => {
    if (!reportId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchReport(reportId, {
        fields: "id,displayTitle,report_number,status,approval_status,customer,executing_employee_user,executing_employee,agreement_date,appointment.date,appointment.time,appointment.time_from,appointment.time_to,appointment.approved,appointment.has_agreement,project,deliveryAddress,full_delivery_address,reportType.displayTitle,reportType.title,description,report,internal_description,internal_report,checklist_id,variables,variable_type_groups,variable_groups,created_at,updated_at,approved,not_approved,process_note,updated_after_process,finished"
      });

      if (response.data) {
        setReport(response.data);
      } else {
        throw new Error("Kunne ikke laste rapport data");
      }
    } catch (error) {
      console.error("Error loading report:", error);
      setError(error.message || "Kunne ikke laste rapport");
      toast.error(t("failed.to.load.report"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportRefresh = (data) => {
    const reportData = data?.data || data;
    setReport((prev) => ({ ...prev, ...reportData }));
    if (onRefresh) {
      onRefresh(reportData);
    }
    loadReport(); // Reload to get fresh data
  };

  if (!open) return null;

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "finished":
      case "processed":
      case "accomplished":
        return "text-green-600";
      case "processing":
      case "under_process":
        return "text-yellow-600";
      case "cancelled":
      case "closed":
        return "text-red-600";
      default:
        return "text-blue-600";
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={report?.report_number ? `Rapport ${report.report_number}` : "Rapport detaljer"}
      size="6xl"
      isLoading={isLoading}
    >
      {error ? (
        <div className="text-center py-8 text-red-500">
          <div className="text-lg font-medium mb-2">Feil ved lasting av rapport</div>
          <div className="text-sm">{error}</div>
          <button
            onClick={loadReport}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Prøv igjen
          </button>
        </div>
      ) : !report && !isLoading ? (
        <div className="text-center py-8 text-gray-500">
          Ingen rapport valgt
        </div>
      ) : !report ? (
        <div className="text-center py-8 text-gray-500">
          Laster rapport...
        </div>
      ) : report ? (
        <div className="space-y-4 md:space-y-6">
          {/* Header Section */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 truncate">
                    Rapport {report.report_number}
                  </h2>
                  <button
                    onClick={() => window.open(`/dashboard/service/reports/${report.id}`, '_blank')}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex-shrink-0"
                    title="Åpne rapport i nytt vindu"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1.5" />
                    <span className="hidden sm:inline">Åpne rapport</span>
                    <span className="sm:hidden">Åpne</span>
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:gap-4">
                  <span className={`font-medium text-sm md:text-base ${getStatusColor(report.status)}`}>
                    {report.status}
                  </span>
                  {report.approval_status && (
                    <StatusBadge status={report.approval_status} type="approval" />
                  )}
                </div>
              </div>
              <div className="text-left md:text-right text-xs md:text-sm text-gray-500 flex-shrink-0">
                <div>Opprettet: {report.created_at ? format(new Date(report.created_at), "dd.MM.yyyy", { locale: nb }) : "Ukjent"}</div>
                <div>Sist oppdatert: {report.updated_at ? format(new Date(report.updated_at), "dd.MM.yyyy", { locale: nb }) : "Ukjent"}</div>
              </div>
            </div>

            {/* Info Grid - Responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              <div className="flex items-start gap-2">
                <UserIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <span className="text-xs md:text-sm text-gray-600">Kunde:</span>
                  <span className="ml-1 font-medium text-sm md:text-base text-gray-900 break-words">{report.customer?.displayTitle || report.customer?.name || "Ukjent"}</span>
                </div>
              </div>

              {(report.executing_employee_user || report.executing_employee) && (
                <div className="flex items-start gap-2">
                  <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="text-xs md:text-sm text-gray-600">Utførende:</span>
                    <span className="ml-1 font-medium text-sm md:text-base text-gray-900 break-words">
                      {report.executing_employee_user?.name || report.executing_employee?.name || "Ukjent"}
                    </span>
                  </div>
                </div>
              )}

              {(report.agreement_date || report.appointment?.date) && (
                <div className="flex items-start gap-2">
                  <CalendarDaysIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="text-xs md:text-sm text-gray-600">Avtalt dato:</span>
                    <span className="ml-1 font-medium text-sm md:text-base text-gray-900">
                      {report.appointment?.date 
                        ? format(new Date(report.appointment.date), "dd.MM.yyyy", { locale: nb })
                        : report.agreement_date 
                        ? format(new Date(report.agreement_date), "dd.MM.yyyy", { locale: nb })
                        : "Ukjent"}
                    </span>
                    {(report.appointment?.time || report.appointment?.time_from || report.appointment?.time_to) && (
                      <span className="ml-1 text-xs text-gray-500">
                        {report.appointment?.time 
                          ? report.appointment.time
                          : report.appointment?.time_from && report.appointment?.time_to
                          ? `${report.appointment.time_from} - ${report.appointment.time_to}`
                          : report.appointment?.time_from
                          ? `fra ${report.appointment.time_from}`
                          : ""}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {report.project && (
                <div className="flex items-start gap-2">
                  <BuildingStorefrontIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="text-xs md:text-sm text-gray-600">Prosjekt:</span>
                    <span className="ml-1 font-medium text-sm md:text-base text-gray-900 break-words">
                      {report.project.project_number} - {report.project.name}
                    </span>
                  </div>
                </div>
              )}

              {report.deliveryAddress && (
                <div className="flex items-start gap-2">
                  <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="text-xs md:text-sm text-gray-600">Adresse:</span>
                    <span className="ml-1 font-medium text-sm md:text-base text-gray-900 break-words">
                      {report.full_delivery_address || report.deliveryAddress.address}
                    </span>
                  </div>
                </div>
              )}

              {report.reportType && (
                <div className="flex items-start gap-2">
                  <DocumentCheckIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="text-xs md:text-sm text-gray-600">Rapport type:</span>
                    <span className="ml-1 font-medium text-sm md:text-base text-gray-900 break-words">
                      {report.reportType.displayTitle || report.reportType.title}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {report.description && (
              <div className="mt-4 p-3 md:p-4 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-2">Beskrivelse:</div>
                <div className="text-sm text-gray-600 prose prose-sm max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: report.description }} />
                </div>
              </div>
            )}

            {/* Internal Description - Admin Only */}
            {report.internal_description && (
              <RoleOnly role="admin">
                <div className="mt-4 p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm font-medium text-blue-800 mb-2">Intern beskrivelse:</div>
                  <div className="text-sm text-blue-700 prose prose-sm max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: report.internal_description }} />
                  </div>
                </div>
              </RoleOnly>
            )}
          </div>

          {/* Approval Section - Admin Only */}
          {report?.finished && (canManageReports || canApproveReports) && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className={`px-4 md:px-6 py-4 md:py-5 ${
                report?.approved ? "bg-green-50 border-b border-green-200" :
                report?.not_approved ? "bg-red-50 border-b border-red-200" :
                report?.updated_after_process ? "bg-yellow-50 border-b border-yellow-200" :
                "bg-blue-50 border-b border-blue-200"
              }`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {report?.approved ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : report?.not_approved ? (
                      <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                    ) : report?.updated_after_process ? (
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                    ) : (
                      <ClockIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="text-base md:text-lg font-semibold text-gray-900">
                        {report?.approved ? "Godkjent" :
                         report?.not_approved ? "Ikke godkjent" :
                         report?.updated_after_process ? "Oppdatert" :
                         "Venter på behandling"}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {report?.approved ? "Rapporten er godkjent" :
                         report?.not_approved ? (report?.process_note || "Rapporten må korrigeres") :
                         report?.updated_after_process ? "Rapporten er oppdatert og klar for behandling på nytt" :
                         "Rapporten er ferdigstilt og venter på godkjenning"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setApproveDialogOpen(true)}
                    className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                      report?.approved
                        ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                        : report?.not_approved
                        ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                        : report?.updated_after_process
                        ? "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
                        : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
                    }`}
                  >
                    {report?.approved || report?.not_approved ? (
                      <>
                        <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                        Behandle på nytt
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        Behandle rapport
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Checklist Section — loaded via GET .../checklist when report has checklist_id */}
          {reportChecklistId && (() => {
            const checklistItems = overviewChecklist?.items || [];
            const approvedCount = checklistItems.filter(item => item.isApproved && !item.isDeviation).length;
            const deviationCount = checklistItems.filter(item => item.isDeviation || (!item.isApproved && item.isDeviation !== undefined)).length;
            const itemCountLabel = overviewChecklistLoading
              ? "…"
              : String(checklistItems.length);

            return (
              <AccordionSection
                title={`Sjekkliste (${itemCountLabel} elementer)`}
                icon={ClipboardDocumentCheckIcon}
                defaultOpen={false}
                rightContent={
                  !overviewChecklistLoading &&
                  checklistItems.length > 0 && (
                    <div className="flex items-center gap-3 md:gap-4 text-sm">
                      {approvedCount > 0 && (
                        <div className="flex items-center gap-1.5">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-700">{approvedCount}</span>
                          <span className="text-gray-500 hidden sm:inline">godkjent</span>
                        </div>
                      )}
                      {deviationCount > 0 && (
                        <div className="flex items-center gap-1.5">
                          <XCircleIcon className="h-4 w-4 text-red-600" />
                          <span className="font-medium text-red-700">{deviationCount}</span>
                          <span className="text-gray-500 hidden sm:inline">avvik</span>
                        </div>
                      )}
                    </div>
                  )
                }
              >
                <div className="space-y-3">
                  {overviewChecklistLoading && (
                    <div className="text-sm text-gray-500">Laster sjekkliste…</div>
                  )}
                  {!overviewChecklistLoading && overviewChecklist && (
                    <>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Status:</span>
                      <StatusBadge status={overviewChecklist.is_checked} type="checklist" />
                    </div>
                    {overviewChecklist.finished_at && (
                      <span className="text-xs text-gray-500">
                        Fullført: {format(new Date(overviewChecklist.finished_at), "dd.MM.yyyy HH:mm", { locale: nb })}
                      </span>
                    )}
                  </div>

                  {checklistItems.length > 0 ? (
                    <div className="space-y-3">
                      {checklistItems.map((item, index) => (
                        <div key={item.id || index} className="flex items-start gap-3 p-3 md:p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex-shrink-0 mt-0.5">
                            <StatusBadge status={item.isDeviation ? false : item.isApproved} type="checklist" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm md:text-base text-gray-900 break-words mb-1">
                              {item.title}
                            </div>
                            {item.description && (
                              <div className="text-sm text-gray-600 mt-2 break-words">
                                {item.description}
                              </div>
                            )}
                            {(item.note || item.comment) && (
                              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="text-xs font-medium text-blue-800 mb-1">Kommentar:</div>
                                <div className="text-sm text-blue-900 break-words">
                                  {item.note || item.comment}
                                </div>
                              </div>
                            )}
                            {item.id && (
                              <div className="mt-3">
                                <TinyDocumentSection
                                  module="checklist_item"
                                  moduleParam={item.id}
                                  title=""
                                  showNoDocuments={false}
                                  maxDocumentDisplay={6}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">Ingen sjekkliste elementer</div>
                  )}
                    </>
                  )}
                  {!overviewChecklistLoading && !overviewChecklist && (
                    <div className="text-sm text-gray-500 italic">Kunne ikke laste sjekkliste.</div>
                  )}
                </div>
              </AccordionSection>
            );
          })()}

          {/* Item Consumption Section — GET .../items */}
          {!overviewItemsLoading && overviewItems.length > 0 && (
            <AccordionSection
              title={`Vareforbruk (${overviewItems.length} varer)`}
              icon={CubeIcon}
              defaultOpen={false}
            >
              <div className="space-y-3">
                <div className="overflow-x-auto -mx-4 md:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Produkt
                          </th>
                          <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Antall
                          </th>
                          <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Enhet
                          </th>
                          <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {overviewItems.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 md:px-4 py-3 text-sm text-gray-900">
                              <div>
                                <div className="font-medium break-words">
                                  {item.product?.displayTitle || item.product?.title || item.description}
                                </div>
                                {item.product?.item_number && (
                                  <div className="text-xs text-gray-500">Art.nr: {item.product.item_number}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-3 md:px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {item.quantity}
                            </td>
                            <td className="px-3 md:px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {item.unit?.short_title || item.unit?.title || "-"}
                            </td>
                            <td className="px-3 md:px-4 py-3 text-sm">
                              <div className="flex flex-col gap-1">
                                {item.picked && (
                                  <span className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full w-fit">
                                    Plukket
                                  </span>
                                )}
                                {(item.used_on_job || item.not_used) && (
                                  <span className="inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full w-fit">
                                    {item.not_used ? "Ikke brukt" : "Brukt på jobb"}
                                  </span>
                                )}
                                {item.returned_to_storage && (
                                  <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full w-fit">
                                    Returnert
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </AccordionSection>
          )}

          {/* Variables Section */}
          {report.variables && report.variables.length > 0 && (
            <AccordionSection
              title={`Variabler (${report.variables.length} variabler)`}
              icon={VariableIcon}
              defaultOpen={false}
            >
              <div className="space-y-4">
                {/* Variable Type Groups */}
                {report.variable_type_groups && report.variable_type_groups.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Variable grupper</h4>
                    <div className="space-y-4">
                      {report.variable_type_groups.map((typeGroup) => (
                        <div key={typeGroup.id} className="border border-gray-200 rounded-lg p-3 md:p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                            <h5 className="font-medium text-gray-900 break-words">
                              {typeGroup.report_variable_type?.title || "Type gruppe"}
                            </h5>
                            <div className="text-xs text-gray-500 flex-shrink-0">
                              Enhet: {formatUnitDisplay(typeGroup.measurement_unit)}
                              {typeGroup.projected_unit && (
                                <> | Prosjektert: {formatUnitDisplay(typeGroup.projected_unit)}</>
                              )}
                            </div>
                          </div>

                          <div className="overflow-x-auto -mx-3 md:mx-0">
                            <div className="inline-block min-w-full align-middle px-3 md:px-0">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-2 md:px-3 py-2 text-left font-medium text-gray-700 text-xs">Referanse</th>
                                    <th className="px-2 md:px-3 py-2 text-left font-medium text-gray-700 text-xs">Prosjektert</th>
                                    <th className="px-2 md:px-3 py-2 text-left font-medium text-gray-700 text-xs">Før-måling</th>
                                    <th className="px-2 md:px-3 py-2 text-left font-medium text-gray-700 text-xs">Målt</th>
                                    <th className="px-2 md:px-3 py-2 text-left font-medium text-gray-700 text-xs">Notat</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {typeGroup.variables && typeGroup.variables.map((variable) => {
                                    const latestMeasurement = variable.measurements?.sort((a, b) =>
                                      new Date(b.created_at) - new Date(a.created_at)
                                    )[0];
                                    const measuredValue = latestMeasurement?.value;

                                    return (
                                      <tr key={variable.id}>
                                        <td className="px-2 md:px-3 py-2 text-gray-900 break-words">{variable.reference}</td>
                                        <td className="px-2 md:px-3 py-2 text-gray-500 whitespace-nowrap">
                                          {variable.projected_quantity ?
                                            formatUnitDisplay(
                                              variable.projected_unit || typeGroup.projected_unit || typeGroup.measurement_unit,
                                              variable.projected_quantity
                                            ) : "-"
                                          }
                                        </td>
                                        <td className="px-2 md:px-3 py-2 text-gray-500 whitespace-nowrap">
                                          {variable.pre_measurement_quantity ?
                                            formatUnitDisplay(
                                              variable.measurement_unit || typeGroup.measurement_unit,
                                              variable.pre_measurement_quantity
                                            ) : "-"
                                          }
                                        </td>
                                        <td className="px-2 md:px-3 py-2 text-gray-500 whitespace-nowrap">
                                          {measuredValue ?
                                            formatUnitDisplay(
                                              variable.measurement_unit || typeGroup.measurement_unit,
                                              measuredValue
                                            ) : "-"
                                          }
                                        </td>
                                        <td className="px-2 md:px-3 py-2 text-gray-500 break-words">{variable.note || "-"}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Legacy Variables */}
                {report.variables.some(v => !v.type_group_id && !v.group_id) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Legacy variabler</h4>
                    <div className="overflow-x-auto -mx-4 md:mx-0">
                      <div className="inline-block min-w-full align-middle">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Referanse
                              </th>
                              <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Felt 1
                              </th>
                              <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Felt 2
                              </th>
                              <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Enhet
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {report.variables
                              .filter(v => !v.type_group_id && !v.group_id)
                              .map((variable) => (
                              <tr key={variable.id}>
                                <td className="px-3 md:px-4 py-3 text-sm text-gray-900 break-words">
                                  {variable.type?.title || variable.report_variable_type?.title || "-"}
                                </td>
                                <td className="px-3 md:px-4 py-3 text-sm text-gray-500 break-words">
                                  {variable.reference || "-"}
                                </td>
                                <td className="px-3 md:px-4 py-3 text-sm text-gray-500 break-words">
                                  {variable.field1 || "-"}
                                </td>
                                <td className="px-3 md:px-4 py-3 text-sm text-gray-500 break-words">
                                  {variable.field2 || "-"}
                                </td>
                                <td className="px-3 md:px-4 py-3 text-sm text-gray-500 break-words">
                                  {variable.unit?.title || "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </AccordionSection>
          )}

          {/* Report Content */}
          {report.report && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="px-4 md:px-6 py-4 md:py-5 border-b border-gray-200">
                <h3 className="text-base md:text-lg font-medium leading-6 text-gray-900 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  Rapport innhold
                </h3>
              </div>
              <div className="px-4 md:px-6 py-4 md:py-5">
                <div className="prose prose-sm max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: report.report }} />
                </div>
              </div>
            </div>
          )}

          {/* Report Documents */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 md:px-6 py-4 md:py-5 border-b border-gray-200">
              <h3 className="text-base md:text-lg font-medium leading-6 text-gray-900 flex items-center gap-2">
                <DocumentIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                Bilder og dokumenter
              </h3>
            </div>
            <div className="px-4 md:px-6 py-4 md:py-5">
              <TinyDocumentSection
                module="report"
                moduleParam={report.id}
                title=""
                showNoDocuments={true}
                maxDocumentDisplay={6}
              />
            </div>
          </div>

          {/* Internal Report Content - Admin Only */}
          {(report.internal_description || report.internal_report) && (
            <RoleOnly role="admin">
              <AccordionSection
                title="Intern informasjon"
                icon={DocumentTextIcon}
                defaultOpen={false}
              >
                <div className="space-y-4">
                  {report.internal_description && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Intern beskrivelse</h4>
                      <div className="text-sm text-gray-600 prose prose-sm max-w-none bg-gray-50 p-3 rounded-lg">
                        <div dangerouslySetInnerHTML={{ __html: report.internal_description }} />
                      </div>
                    </div>
                  )}

                  {report.internal_report && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Intern rapport</h4>
                      <div className="prose prose-sm max-w-none bg-gray-50 p-3 rounded-lg">
                        <div dangerouslySetInnerHTML={{ __html: report.internal_report }} />
                      </div>
                    </div>
                  )}
                </div>
              </AccordionSection>
            </RoleOnly>
          )}

          {/* Economic Summary Section - Only for Admin */}
          {reportFinancialSummary && (
            <RoleOnly role="admin">
              <AccordionSection
                title="Økonomisk oversikt"
                icon={ChartBarIcon}
                defaultOpen={false}
                rightContent={
                  <div className="flex items-center gap-4 md:gap-6 text-sm md:text-base">
                    <div className="text-right">
                      <div className="font-bold text-gray-900">
                        {new Intl.NumberFormat("nb-NO", {
                          style: "currency",
                          currency: "NOK",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(reportFinancialSummary.profit || 0)}
                      </div>
                      <div className="text-xs text-gray-500">Fortjeneste</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-900">
                        {reportFinancialSummary.profit_margin || 0}%
                      </div>
                      <div className="text-xs text-gray-500">Margin</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-900">
                        {`${new Intl.NumberFormat("nb-NO", {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        }).format(
                          reportFinancialSummary.hours?.total ||
                            reportFinancialSummary.total_hours ||
                            reportFinancialSummary.hours?.registered ||
                            0
                        )}t`}
                      </div>
                      <div className="text-xs text-gray-500">Timer</div>
                    </div>
                  </div>
                }
              >
                <ReportSummarySection
                  report={report}
                  summary={reportFinancialSummary}
                  summaryLoading={reportFinancialSummaryLoading}
                />
              </AccordionSection>
            </RoleOnly>
          )}
        </div>
      ) : null}

      {/* Approval Dialog */}
      {report && (
        <ApproveReportDialog
          open={approveDialogOpen}
          report={report}
          onClose={() => setApproveDialogOpen(false)}
          onRefresh={handleReportRefresh}
        />
      )}
    </PMDialog>
  );
};

export default ReportOverviewSummary;
